import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  Typography,
  Button,
  TextField,
  Grid,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Container,
  Paper,
  Box,
  Divider
} from '@mui/material';
import { API } from '../../config';
import { getApiErrorMessage } from '../../utils/apiError';

const states = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida',
  'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine',
  'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska',
  'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas',
  'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

const formatPhoneNumber = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 10);

  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const formatDateOfBirth = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 8);

  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;

  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const convertDobToBackendFormat = (dob) => {
  const [month, day, year] = dob.split('/');
  return `${year}-${month}-${day}`;
};

const isValidDateOfBirth = (dob) => {
  const parts = dob.split('/');
  if (parts.length !== 3) return false;

  const month = Number(parts[0]);
  const day = Number(parts[1]);
  const year = Number(parts[2]);

  if (!month || !day || !year) return false;
  if (year < 1900) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  const birthDate = new Date(year, month - 1, day);

  if (
    birthDate.getFullYear() !== year ||
    birthDate.getMonth() !== month - 1 ||
    birthDate.getDate() !== day
  ) {
    return false;
  }

  const today = new Date();
  if (birthDate > today) return false;

  const ageDate = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate()
  );

  if (birthDate > ageDate) return false;

  return true;
};

const getLegalNameError = (value, label) => {
  const name = String(value || '').trim().replace(/\s+/g, ' ');

  if (!name) {
    return `${label} is required.`;
  }

  if (name.length > 100) {
    return `${label} must be 100 characters or fewer.`;
  }

  if (!/^[\p{L}\p{M}][\p{L}\p{M}'’ -]*$/u.test(name)) {
    return `${label} may contain letters, spaces, apostrophes, or hyphens only.`;
  }

  return '';
};

const isValidEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

const CustomerSignUp = () => {
  const history = useHistory();

  const [contactInfo, setContactInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    dateOfBirth: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    customerType: '',
    businessName: '',
    businessContact: '',
    ein: '',
  });

  const [acceptTerms, setAcceptTerms] = useState(false);
  const [confirmLegalIdentity, setConfirmLegalIdentity] = useState(false);

  const handleContactInfoChange = (field, value) => {
    setContactInfo({ ...contactInfo, [field]: value });
  };

  const handleCustomerSignUpSubmit = async (e) => {
    e.preventDefault();

    if (!confirmLegalIdentity) {
      alert('Please confirm that your signup information is accurate and matches your legal identification or business records.');
      return;
    }

    if (!acceptTerms) {
      alert('Please agree to the Terms, Privacy Policy, and Customer Agreement before creating your account.');
      return;
    }

    const firstNameError = getLegalNameError(contactInfo.firstName, 'Legal first name');

    if (firstNameError) {
      alert(firstNameError);
      return;
    }

    const lastNameError = getLegalNameError(contactInfo.lastName, 'Legal last name');

    if (lastNameError) {
      alert(lastNameError);
      return;
    }

    if (!isValidEmail(contactInfo.email)) {
      alert('Please enter a valid email address.');
      return;
    }

    if (!isValidDateOfBirth(contactInfo.dateOfBirth)) {
      alert('Please enter a valid date of birth in MM/DD/YYYY format. You must be at least 18 years old.');
      return;
    }

    const phoneDigits = contactInfo.phoneNumber.replace(/\D/g, '');

    if (phoneDigits.length !== 10) {
      alert('Please enter a valid 10-digit U.S. phone number.');
      return;
    }

    if (!contactInfo.address || !contactInfo.address.trim()) {
      alert('Street Address is required.');
      return;
    }

    if (!contactInfo.city || !contactInfo.city.trim()) {
      alert('City is required.');
      return;
    }

    if (!contactInfo.state || !contactInfo.state.trim()) {
      alert('State is required.');
      return;
    }

    if (!/^\d{5}$/.test(contactInfo.zipCode)) {
      alert('Zip Code must be 5 digits.');
      return;
    }

    if (!contactInfo.customerType) {
      alert('Please select an account type.');
      return;
    }

    if (contactInfo.customerType === 'business') {
      if (!contactInfo.businessContact || !contactInfo.businessContact.trim()) {
        alert('Business Contact Person is required.');
        return;
      }

      if (!contactInfo.businessName || !contactInfo.businessName.trim()) {
        alert('Business Name is required.');
        return;
      }

      if (!contactInfo.ein || !contactInfo.ein.trim()) {
        alert('EIN / Tax ID is required.');
        return;
      }
    }

    if (contactInfo.password.length < 8) {
      alert('Password must be at least 8 characters long.');
      return;
    }

    if (contactInfo.password !== contactInfo.confirmPassword) {
      alert('Passwords do not match.');
      return;
    }

    const { confirmPassword, ...cleanContactInfo } = contactInfo;

    const submissionData = {
      ...cleanContactInfo,
      firstName: contactInfo.firstName.trim().replace(/\s+/g, ' '),
      lastName: contactInfo.lastName.trim().replace(/\s+/g, ' '),
      email: contactInfo.email.trim().toLowerCase(),
      password: contactInfo.password.trim(),
      address: contactInfo.address.trim(),
      city: contactInfo.city.trim(),
      state: contactInfo.state,
      zipCode: contactInfo.zipCode.trim(),
      customerType: contactInfo.customerType,
      businessName: contactInfo.businessName.trim(),
      businessContact: contactInfo.businessContact.trim(),
      ein: contactInfo.ein.trim(),
      country: 'United States',
      phoneNumber: phoneDigits,
      dateOfBirth: convertDobToBackendFormat(contactInfo.dateOfBirth),
      legalIdentityConfirmed: confirmLegalIdentity,
    };

    console.log('Submitted data:', submissionData);

    try {
      const response = await fetch(`${API}/api/auth/register-customer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submissionData),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        console.log('Customer registered successfully:', data);

        if (!data.token) {
          alert('Customer account created successfully. Please sign in.');
          history.push('/customer/sign-in');
          return;
        }

        localStorage.setItem('token', data.token);
        localStorage.setItem('customer_token', data.token);
        localStorage.setItem('userType', 'customer');
        localStorage.setItem('role', 'customer');
        localStorage.setItem('customerType', contactInfo.customerType || 'individual');

        alert('Customer account created successfully. You are now signed in.');
        history.push('/customer/dashboard');
      } else {
        console.error('Registration failed:', data);
        alert(
          getApiErrorMessage(
            data,
            'Unable to create account. Please review the required fields and try again.'
          )
        );
      }
    } catch (error) {
      console.error('Network or server error:', error);
      alert('Unable to connect to the server. Please check your connection and try again.');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        py: { xs: 4, md: 6 },
      }}
    >
      <Container
        maxWidth="md"
        sx={{
          px: { xs: 2.25, sm: 3 },
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2.5, sm: 3.5, md: 5 },
            borderRadius: 4,
            border: '1px solid rgba(15, 23, 42, 0.08)',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              textAlign: 'center',
              mb: 3,
              maxWidth: 560,
              mx: 'auto',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: '1.95rem', sm: '2.2rem', md: '2.35rem' },
                fontWeight: 800,
                lineHeight: 1.15,
                letterSpacing: '-0.035em',
                color: '#0f172a',
                mb: 1,
              }}
            >
              Customer Sign Up
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: '#64748b',
                maxWidth: 500,
                mx: 'auto',
                lineHeight: 1.55,
                mb: 3,
                fontSize: { xs: '0.98rem', sm: '1rem' },
              }}
            >
              Create a HaggleFreight customer account to post loads, manage transportation requests, track updates, and work directly with drivers or carriers.
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1.25,
                maxWidth: 430,
                mx: 'auto',
                mb: 2,
              }}
            >
              <Button
                component={Link}
                to="/customer/sign-up"
                variant="contained"
                color="primary"
                fullWidth
                sx={{
                  minHeight: 44,
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  borderRadius: 2,
                  whiteSpace: 'nowrap',
                  fontSize: { xs: '0.88rem', sm: '0.86rem', md: '0.9rem' },
                  letterSpacing: '0.025em',
                }}
              >
                CREATE ACCOUNT
              </Button>

              <Button
                component={Link}
                to="/customer/sign-in"
                variant="outlined"
                color="primary"
                fullWidth
                sx={{
                  minHeight: 44,
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  borderRadius: 2,
                  whiteSpace: 'nowrap',
                  fontSize: { xs: '0.88rem', sm: '0.86rem', md: '0.9rem' },
                  letterSpacing: '0.025em',
                }}
              >
                SIGN IN
              </Button>
            </Box>

            <Button
              component={Link}
              to="/"
              variant="text"
              sx={{
                textTransform: 'uppercase',
                fontWeight: 800,
                fontSize: '0.82rem',
                letterSpacing: '0.04em',
                mb: 2,
              }}
            >
              BACK TO HOME
            </Button>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <form onSubmit={handleCustomerSignUpSubmit} noValidate>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                color: '#0f172a',
                mb: 1,
              }}
            >
              Contact Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Legal First Name"
                  variant="outlined"
                  fullWidth
                  value={contactInfo.firstName}
                  onChange={(e) =>
                    handleContactInfoChange('firstName', e.target.value)
                  }
                  helperText="Enter your legal first name as shown on your government ID."
                  autoComplete="given-name"
                  inputProps={{ maxLength: 100 }}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Legal Last Name"
                  variant="outlined"
                  fullWidth
                  value={contactInfo.lastName}
                  onChange={(e) =>
                    handleContactInfoChange('lastName', e.target.value)
                  }
                  helperText="Enter your legal last name as shown on your government ID."
                  autoComplete="family-name"
                  inputProps={{ maxLength: 100 }}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  type="text"
                  variant="outlined"
                  fullWidth
                  value={contactInfo.email}
                  onChange={(e) =>
                    handleContactInfoChange('email', e.target.value)
                  }
                  autoComplete="email"
                  inputProps={{
                    inputMode: 'email',
                    autoCapitalize: 'none',
                    spellCheck: false,
                  }}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Create Password"
                  type="password"
                  variant="outlined"
                  fullWidth
                  value={contactInfo.password}
                  onChange={(e) =>
                    handleContactInfoChange('password', e.target.value)
                  }
                  helperText="Minimum 8 characters"
                  autoComplete="new-password"
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Confirm Password"
                  type="password"
                  variant="outlined"
                  fullWidth
                  value={contactInfo.confirmPassword}
                  onChange={(e) =>
                    handleContactInfoChange('confirmPassword', e.target.value)
                  }
                  autoComplete="new-password"
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone Number"
                  placeholder="(555) 123-4567"
                  variant="outlined"
                  fullWidth
                  value={contactInfo.phoneNumber}
                  onChange={(e) =>
                    handleContactInfoChange(
                      'phoneNumber',
                      formatPhoneNumber(e.target.value)
                    )
                  }
                  helperText="Format: (555) 123-4567"
                  inputProps={{
                    inputMode: 'tel',
                    maxLength: 14,
                  }}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date of Birth"
                  placeholder="MM/DD/YYYY"
                  variant="outlined"
                  fullWidth
                  value={contactInfo.dateOfBirth}
                  onChange={(e) =>
                    handleContactInfoChange(
                      'dateOfBirth',
                      formatDateOfBirth(e.target.value)
                    )
                  }
                  helperText="MM/DD/YYYY"
                  inputProps={{
                    inputMode: 'numeric',
                    maxLength: 10,
                  }}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Street Address"
                  variant="outlined"
                  fullWidth
                  value={contactInfo.address}
                  onChange={(e) =>
                    handleContactInfoChange('address', e.target.value)
                  }
                  autoComplete="street-address"
                  required
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="City"
                  variant="outlined"
                  fullWidth
                  value={contactInfo.city}
                  onChange={(e) =>
                    handleContactInfoChange('city', e.target.value)
                  }
                  autoComplete="address-level2"
                  required
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  label="State"
                  variant="outlined"
                  fullWidth
                  value={contactInfo.state}
                  onChange={(e) =>
                    handleContactInfoChange('state', e.target.value)
                  }
                  autoComplete="address-level1"
                  required
                >
                  {states.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Zip Code"
                  variant="outlined"
                  fullWidth
                  value={contactInfo.zipCode}
                  onChange={(e) =>
                    handleContactInfoChange(
                      'zipCode',
                      e.target.value.replace(/\D/g, '').slice(0, 5)
                    )
                  }
                  inputProps={{
                    maxLength: 5,
                    inputMode: 'numeric',
                  }}
                  autoComplete="postal-code"
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />

                <Typography
                  variant="h6"
                  sx={{
                    textAlign: 'center',
                    fontWeight: 800,
                    color: '#0f172a',
                    mb: 2,
                  }}
                >
                  Account Type
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                sm={contactInfo.customerType === 'business' ? 6 : 12}
              >
                <Box
                  sx={{
                    width: {
                      xs: '100%',
                      sm: contactInfo.customerType === 'business' ? '100%' : 460,
                    },
                    maxWidth: '100%',
                    mx: contactInfo.customerType === 'business' ? 0 : 'auto',
                  }}
                >
                  <TextField
                    select
                    label="Account Type"
                    variant="outlined"
                    fullWidth
                    value={contactInfo.customerType}
                    onChange={(e) =>
                      handleContactInfoChange('customerType', e.target.value)
                    }
                    required
                  >
                    <MenuItem value="individual">Individual</MenuItem>
                    <MenuItem value="business">Business</MenuItem>
                  </TextField>
                </Box>
              </Grid>

              {contactInfo.customerType === 'business' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Business Contact Person"
                      variant="outlined"
                      fullWidth
                      value={contactInfo.businessContact}
                      onChange={(e) =>
                        handleContactInfoChange('businessContact', e.target.value)
                      }
                      required
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Business Name"
                      variant="outlined"
                      fullWidth
                      value={contactInfo.businessName}
                      onChange={(e) =>
                        handleContactInfoChange('businessName', e.target.value)
                      }
                      required
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="EIN / Tax ID"
                      variant="outlined"
                      fullWidth
                      placeholder="12-3456789"
                      inputProps={{ maxLength: 10 }}
                      value={contactInfo.ein}
                      onChange={(e) =>
                        handleContactInfoChange('ein', e.target.value.slice(0, 10))
                      }
                      required
                    />
                  </Grid>
                </>
              )}
            </Grid>

            <FormControlLabel
              sx={{
                mt: 3,
                display: 'flex',
                alignItems: 'flex-start',
                textAlign: 'left',
              }}
              control={
                <Checkbox
                  checked={confirmLegalIdentity}
                  onChange={(e) => setConfirmLegalIdentity(e.target.checked)}
                  color="primary"
                  required
                />
              }
              label={
                <Typography variant="body2">
                  I confirm that my legal name, date of birth, phone number, and
                  address are accurate and match my government ID or business records.
                </Typography>
              }
            />

            <Typography
              variant="caption"
              display="block"
              align="left"
              color="text.secondary"
              sx={{ mt: 1 }}
            >
              HaggleFreight may use this information for account security, fraud
              prevention, payment disputes, and shipment-related support.
            </Typography>

            <FormControlLabel
              sx={{
                mt: 3,
                display: 'flex',
                alignItems: 'flex-start',
                textAlign: 'left',
              }}
              control={
                <Checkbox
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  color="primary"
                  required
                />
              }
              label={
                <Typography variant="body2">
                  I agree to the{' '}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer">
                    Terms
                  </Link>
                  ,{' '}
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </Link>
                  , and{' '}
                  <Link
                    to="/customer-agreement"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Customer Agreement
                  </Link>
                  .
                </Typography>
              }
            />

            <Typography
              variant="caption"
              display="block"
              align="left"
              color="text.secondary"
              sx={{ mt: 1 }}
            >
              By creating a customer account, you agree to HaggleFreight’s platform
              rules, payment policies, load requirements, and legal agreements.
            </Typography>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={!acceptTerms || !confirmLegalIdentity}
              fullWidth
              sx={{
                mt: 3,
                minHeight: 46,
                fontWeight: 800,
                textTransform: 'uppercase',
                borderRadius: 2,
                letterSpacing: '0.025em',
              }}
            >
              CREATE CUSTOMER ACCOUNT
            </Button>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default CustomerSignUp;